import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useCheckoutEmbedControls, WhopCheckoutEmbed } from '@whop/checkout/react';
import { useTagadaContext } from '../providers/TagadaProvider';
import { useTranslation } from '../hooks/useTranslation';
import { useWhopPaymentPolling } from '../hooks/useWhopPaymentPolling';
export const WhopCheckout = memo(forwardRef(({ checkoutSessionId, storeId, customerEmail, shippingAddress, orderItemsCount, orderTotalAmount, onPaymentCompleted, onPaymentFailed, onReady, }, ref) => {
    const { t } = useTranslation();
    const { apiService } = useTagadaContext();
    const whopRef = useCheckoutEmbedControls();
    const isWhopPaymentSubmitted = useRef(false);
    const orderIdRef = useRef(undefined);
    const [planId, setPlanId] = useState(undefined);
    const [isLoadingSession, setIsLoadingSession] = useState(true);
    const [isPaymentLoading, setIsPaymentLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isSessionInitiated = useRef(false);
    // Initialize Whop session to get planId
    const initiateWhopSession = async () => {
        setIsLoadingSession(true);
        try {
            const data = await apiService.fetch(`/api/v1/checkout-sessions/${checkoutSessionId}/whop-payment`, {
                method: 'POST',
                body: JSON.stringify({ checkoutSessionId, storeId }),
                headers: { 'Content-Type': 'application/json' },
            });
            if (data?.planId) {
                setPlanId(data.planId);
            }
        }
        catch (err) {
            console.error('Failed to initialize Whop session:', err);
            setError('Failed to initialize checkout. Please try again.');
        }
        finally {
            setIsLoadingSession(false);
        }
    };
    useEffect(() => {
        if (!checkoutSessionId || !storeId || isSessionInitiated.current)
            return;
        isSessionInitiated.current = true;
        initiateWhopSession();
    }, [checkoutSessionId, storeId]);
    // Re-initiate Whop session when order summary changes (items added/removed, coupons, etc.)
    useEffect(() => {
        if (!checkoutSessionId || !orderItemsCount || !planId)
            return;
        console.log('Regenerating Whop plan due to order changes...');
        isEmbedReady.current = false;
        initiateWhopSession();
    }, [orderItemsCount, orderTotalAmount]);
    // Sync address/email to the Whop embed
    const isEmbedReady = useRef(false);
    const syncAddressAndEmail = () => {
        if (!whopRef.current)
            return;
        if (shippingAddress) {
            const address = {
                name: `${shippingAddress.firstName || ''} ${shippingAddress.lastName || ''}`.trim(),
                country: shippingAddress.country || '',
                line1: shippingAddress.address1 || '',
                city: shippingAddress.city || '',
                state: shippingAddress.state || '',
                postalCode: shippingAddress.postal || '',
            };
            if (typeof whopRef.current.setAddress === 'function') {
                try {
                    whopRef.current.setAddress(address);
                }
                catch {
                    // Whop embed may not be ready yet
                }
            }
        }
        if (customerEmail && typeof whopRef.current.setEmail === 'function') {
            try {
                whopRef.current.setEmail(customerEmail);
            }
            catch {
                // Whop embed may not be ready yet
            }
        }
    };
    // Re-sync when address/email props change after embed is ready
    useEffect(() => {
        if (!isEmbedReady.current)
            return;
        syncAddressAndEmail();
    }, [shippingAddress, customerEmail]);
    // Payment polling
    const { startPolling, isPolling } = useWhopPaymentPolling({
        apiService,
        checkoutSessionId,
        maxAttempts: 60,
        intervalMs: 2000,
        onMaxAttemptsReached: () => {
            isWhopPaymentSubmitted.current = false;
            setIsPaymentLoading(false);
            setError('Payment verification timeout. Please check your email for order confirmation.');
            onPaymentFailed?.('Payment verification timeout.');
        },
        onSuccess: (payment) => {
            isWhopPaymentSubmitted.current = false;
            setIsPaymentLoading(false);
            onPaymentCompleted?.(payment);
        },
        onError: (err) => {
            isWhopPaymentSubmitted.current = false;
            setIsPaymentLoading(false);
            setError('Unable to verify payment. Please check your email for order confirmation.');
            onPaymentFailed?.(err.message);
        },
    });
    // Submit payment handler - called from parent via ref
    const handleWhopPayment = async () => {
        if (isWhopPaymentSubmitted.current) {
            console.warn('Whop payment already submitted.');
            return;
        }
        if (!whopRef.current) {
            console.error('Whop is not available. Please refresh the page.');
            setError('Whop checkout is not available. Please refresh the page.');
            onPaymentFailed?.('Whop checkout is not available.');
            return;
        }
        isWhopPaymentSubmitted.current = true;
        setError(null);
        try {
            if (!planId) {
                isWhopPaymentSubmitted.current = false;
                setError('No plan available for payment.');
                onPaymentFailed?.('No plan available for payment.');
                return;
            }
            const result = await apiService.fetch(`/api/v1/checkout-sessions/${checkoutSessionId}/whop-prepare-order`, {
                method: 'POST',
                body: JSON.stringify({ checkoutSessionId, storeId, planId }),
                headers: { 'Content-Type': 'application/json' },
            });
            if (!result) {
                isWhopPaymentSubmitted.current = false;
                setError('Failed to prepare order.');
                onPaymentFailed?.('Failed to prepare order.');
                return;
            }
            orderIdRef.current = result.orderId;
            whopRef.current.submit();
        }
        catch (err) {
            console.error('Error preparing Whop order:', err);
            isWhopPaymentSubmitted.current = false;
            setError(err?.message || 'Failed to process payment.');
            onPaymentFailed?.(err?.message || 'Failed to process payment.');
        }
    };
    // Whop onComplete callback
    const handleWhopPaymentCompleted = async (receiptId) => {
        isWhopPaymentSubmitted.current = false;
        if (!receiptId) {
            setError('No receipt ID received from Whop.');
            onPaymentFailed?.('No receipt ID received from Whop.');
            return;
        }
        if (!orderIdRef.current) {
            setError('No order ID found.');
            onPaymentFailed?.('No order ID found.');
            return;
        }
        // Update order with latest checkout session data
        try {
            const updatedOrder = await apiService.fetch(`/api/v1/checkout-sessions/${checkoutSessionId}/update-order`, {
                method: 'POST',
                body: JSON.stringify({ checkoutSessionId, orderId: orderIdRef.current }),
                headers: { 'Content-Type': 'application/json' },
            });
            if (!updatedOrder?.id) {
                setError('Error updating order.');
                onPaymentFailed?.('Error updating order.');
                return;
            }
            setIsPaymentLoading(true);
            startPolling(receiptId, updatedOrder.id);
        }
        catch (err) {
            console.error('Error updating order:', err);
            setError('Error finalizing order.');
            onPaymentFailed?.('Error finalizing order.');
        }
    };
    // Expose submit method to parent
    useImperativeHandle(ref, () => ({
        submit: handleWhopPayment,
    }));
    if (isPaymentLoading || isLoadingSession) {
        return (_jsx("div", { className: "rounded-lg border border-[var(--border-color)] bg-[var(--background-color)] p-6", children: _jsxs("div", { className: "py-8 text-center", children: [_jsx("div", { className: "mx-auto h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[var(--primary-color)]" }), _jsx("p", { className: "mt-4 text-sm text-[var(--text-secondary-color)]", children: planId ? 'Updating checkout...' : 'Initializing checkout...' })] }) }));
    }
    if (!planId) {
        return (_jsx("div", { className: "rounded-lg border border-[var(--border-color)] bg-[var(--background-color)] p-6", children: _jsx("div", { className: "py-8 text-center", children: _jsx("p", { className: "text-sm text-red-600", children: "Failed to initialize checkout. Please try again." }) }) }));
    }
    return (_jsxs(_Fragment, { children: [loading && (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50", children: _jsx("div", { className: "rounded-lg bg-white p-8 shadow-xl", children: _jsxs("div", { className: "flex flex-col items-center", children: [_jsx("div", { className: "h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[var(--primary-color)]" }), _jsx("p", { className: "mt-4 text-base font-medium text-gray-900", children: "Payment in progress" })] }) }) })), _jsxs("div", { className: "rounded-lg border border-[var(--border-color)] bg-[var(--background-color)]", children: [_jsx(WhopCheckoutEmbed, { planId: planId, hidePrice: true, ref: whopRef, hideEmail: true, theme: "light", prefill: {
                            email: customerEmail || '',
                            address: shippingAddress
                                ? {
                                    name: `${shippingAddress.firstName || ''} ${shippingAddress.lastName || ''}`.trim(),
                                    country: shippingAddress.country || '',
                                    line1: shippingAddress.address1 || '',
                                    city: shippingAddress.city || '',
                                    state: shippingAddress.state || '',
                                    postalCode: shippingAddress.postal || '',
                                }
                                : undefined,
                        }, hideAddressForm: true, skipRedirect: true, onStateChange: (state) => {
                            setError(null);
                            if (state === 'ready') {
                                isEmbedReady.current = true;
                                syncAddressAndEmail();
                                onReady?.();
                                if (isWhopPaymentSubmitted.current) {
                                    isWhopPaymentSubmitted.current = false;
                                    onPaymentFailed?.('Payment was not completed.');
                                }
                            }
                            if (state === 'loading') {
                                setLoading(true);
                            }
                            else {
                                setLoading(false);
                            }
                        }, onComplete: (_planId, receiptId) => handleWhopPaymentCompleted(receiptId), onAddressValidationError: (err) => {
                            setError(err.error_message);
                            isWhopPaymentSubmitted.current = false;
                            onPaymentFailed?.(err.error_message);
                        }, hideSubmitButton: true }, planId), error && (_jsx("div", { className: "border-t border-[var(--border-color)] p-4", children: _jsx("div", { className: "rounded-md bg-red-50 p-3", children: _jsx("p", { className: "text-sm text-red-800", children: error }) }) }))] })] }));
}));
WhopCheckout.displayName = 'WhopCheckout';
